/** @jsx jsx */
import React, { Component } from 'react';
import { cssMap, cx, jsx } from '@compiled/react';
import type { PreloadedQuery } from 'react-relay';
import { components, type OptionProps } from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import type { ServerSuggestions } from '@atlassian/jira-issue-internal-field-select/src/common/select-inline-edit/select-field/types.tsx';
import { selectFooterStyles } from '@atlassian/jira-issue-internal-field-select/src/common/styled.tsx';
import MultiSelectInlineEditView, {
	type Props as MultiSelectProps,
} from '@atlassian/jira-issue-internal-field-select/src/multi-select-inline-edit/index.tsx';
import type { createReleaseModalQuery as createReleaseModalQueryType } from '@atlassian/jira-relay/src/__generated__/createReleaseModalQuery.graphql';
import type { Version } from '@atlassian/jira-software-releases-release-modals-relay/src/common/ui/types.tsx';
import { CreateReleaseModal } from '@atlassian/jira-software-releases-release-modals-relay/src/ui/create-release-modal/async.tsx';
import { CreateVersionButton } from './create-version-button/index.tsx';
import { prefilterAndLimitItems } from './utils.tsx';
import type { Props } from './types.tsx';

const optionStyles = cssMap({
	root: {
		// apply styles only for 'Create new' option: https://github.com/JedWatson/react-select/issues/3377
		'&::after': {
			// need this element to avoid having another option part being shown below Create one on scrolling (it is shown because options list element has padding)
			content: '""',
			display: 'block',
			position: 'absolute',
			bottom: token('space.negative.100'),
			left: token('space.0'),
			width: '100%',
			height: token('space.100'), // to have same footer height both when list scrolling is at the top and at the very bottom.
			backgroundColor: token('elevation.surface'),
			borderBottomLeftRadius: '4px', // need to match menu's border-radius, not to hide menu's border
			borderBottomRightRadius: '4px',
		},
	},
	focused: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'> .issue-view__select-footer': {
			backgroundColor: token('color.background.neutral'),
		},
	},
});

type State = {
	isDialogOpen: boolean;
	data: ServerSuggestions;
	loading: boolean;
	error: boolean;
	inputQueryStr: string;
	createReleaseModalQueryReference:
		| PreloadedQuery<createReleaseModalQueryType, Record<string, unknown>>
		| null
		| undefined;
};

// eslint-disable-next-line jira/react/no-class-components
class FixVersionsViewClass extends Component<Props, State> {
	state = {
		isDialogOpen: false,
		data: [],
		loading: false,
		error: false,
		inputQueryStr: '',
		createReleaseModalQueryReference: null,
	};

	componentDidMount = () => {
		this.getDataFromCache();
	};

	getDataFromCache = () => {
		this.props.getDataFromCache?.().then((data: ServerSuggestions) => {
			this.setState({ data, loading: false });
		});
	};

	fetchSuggestions = (query: string, sessionId?: string) =>
		this.props.fetchSuggestions(query, sessionId).then((data) => {
			const { inputQueryStr } = this.state;

			const filteredAndSlicedData = prefilterAndLimitItems(data, inputQueryStr);

			this.setState({
				data: filteredAndSlicedData,
			});
			return filteredAndSlicedData;
		});

	openDialog = () => {
		this.setState({ isDialogOpen: true });
	};

	closeDialog = () => {
		this.setState({ isDialogOpen: false });
	};

	setQueryRef = (
		queryRef:
			| PreloadedQuery<createReleaseModalQueryType, Record<string, unknown>>
			| null
			| undefined,
	) => {
		this.setState({ createReleaseModalQueryReference: queryRef });
	};

	onInputChange = (inputQueryStr: string) => {
		this.setState({ inputQueryStr });
	};

	onSuccess = (version: Version) => {
		const { onChange, value, onVersionCreated, versionFieldIds } = this.props;

		const versionARI = { ari: version.ari };

		// dispatch a change event which will add the new version to the list and focus the field again
		onChange([...value, { content: version.name, value: version.id, ...versionARI }]);

		this.closeDialog();

		onVersionCreated(version, versionFieldIds);
	};

	renderSelect(selectProps: MultiSelectProps) {
		const { canAdministerProject, canAdministerJira, createNewItemMessage } = this.props;

		const shouldShowFooter = canAdministerProject || canAdministerJira;
		const { data, loading, error } = this.state;
		const initialData = { data, loading, error };

		const createNewItemIconLabel = { createNewItemIconLabel: this.props.createNewItemIconLabel };

		return (
			<MultiSelectInlineEditView
				{...selectProps}
				classNamePrefix="issue-view-fix-versions"
				styles={selectFooterStyles}
				onInputChange={this.onInputChange}
				shouldShowFooter={shouldShowFooter}
				footer={
					<CreateVersionButton
						createNewItemLabel={createNewItemMessage}
						onCreate={this.setQueryRef}
						{...createNewItemIconLabel}
					/>
				}
				components={{
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					Option: (props: OptionProps<any>) => (
						<components.Option
							{...props}
							xcss={
								props.data.__isNew__ === true &&
								// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
								cx(optionStyles.root, props.isFocused && optionStyles.focused)
							}
						/>
					),
				}}
				onFooterSelect={this.openDialog}
				allowEmptyValue
				optionValuesSafeForAnalytics
				fetchSuggestions={this.fetchSuggestions}
				debounceFetchSuggestionsTime={300}
				initialData={initialData}
				customEditView={this.props.customEditView}
			/>
		);
	}

	render() {
		const {
			baseUrl,
			projectId,
			createNewItemMessage,
			canAdministerProject,
			canAdministerJira,
			onVersionCreated,
			versionFieldIds,
			...selectProps
		} = this.props;
		const { isDialogOpen, createReleaseModalQueryReference } = this.state;

		return (
			<>
				{isDialogOpen && createReleaseModalQueryReference != null && (
					<CreateReleaseModal
						queryReference={createReleaseModalQueryReference}
						onClose={this.closeDialog}
						projectId={String(projectId)}
						onReleaseCreation={this.onSuccess}
					/>
				)}
				{this.renderSelect(selectProps)}
			</>
		);
	}
}

export default FixVersionsViewClass;
